import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, BarChart3, Globe, Search, FileText, Users, 
  DollarSign, Target, Eye, MousePointer, Clock, Calendar, Plus, Edit, 
  Trash2, ChevronRight, CheckCircle, AlertCircle, Lightbulb, ArrowUp, 
  ArrowDown, ExternalLink, Play, Pause, Check, X, Send, MessageSquare,
  Sparkles, Zap, ShoppingCart, Download, Filter, RefreshCw, Settings,
  PieChart, Activity, Share2, Mail, Phone, Star, Award, Briefcase,
  Layout, Image, Video, Megaphone, CreditCard, Bot, ChevronDown, Loader2,
  XCircle, EyeOff, Wand2, Link, Unlink, Youtube, Facebook, Instagram,
  Linkedin, Twitter, ChevronUp
} from 'lucide-react';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

// TikTok icon component
const TikTokIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

// Platform icons component
const PlatformIcon = ({ platform, size = 20 }) => {
  // Handle TikTok separately since it's a custom SVG
  if (platform === 'tiktok') {
    return <TikTokIcon size={size} />;
  }
  
  const icons = {
    website: Globe,
    google_analytics: BarChart3,
    google_search_console: Search,
    youtube: Youtube,
    facebook: Facebook,
    instagram: Instagram,
    linkedin: Linkedin,
    twitter: Twitter,
    mailchimp: Mail,
    hubspot: Users,
    semrush: Target,
    ahrefs: Link
  };
  const Icon = icons[platform] || Globe;
  return <Icon size={size} />;
};

// ============== CONNECTIONS PANEL ==============
const ConnectionsPanel = ({ connections, onConnect, onDisconnect, isExpanded, onToggle }) => {
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [credentials, setCredentials] = useState({});

  const platforms = [
    { id: 'website', name: 'Website', description: 'Connect your website for traffic analytics', color: 'cyan', fields: ['url', 'tracking_id'] },
    { id: 'google_analytics', name: 'Google Analytics', description: 'Track website visitors and behavior', color: 'yellow', fields: ['property_id', 'api_key'] },
    { id: 'google_search_console', name: 'Search Console', description: 'Monitor search performance', color: 'blue', fields: ['site_url', 'api_key'] },
    { id: 'youtube', name: 'YouTube', description: 'Track video performance', color: 'red', fields: ['channel_id', 'api_key'] },
    { id: 'facebook', name: 'Facebook', description: 'Manage Facebook marketing', color: 'blue', fields: ['page_id', 'access_token'] },
    { id: 'instagram', name: 'Instagram', description: 'Track Instagram engagement', color: 'pink', fields: ['username', 'access_token'] },
    { id: 'tiktok', name: 'TikTok', description: 'Short-form video marketing via Outstand', color: 'white', fields: [], oauth: true },
    { id: 'linkedin', name: 'LinkedIn', description: 'B2B marketing analytics', color: 'blue', fields: ['company_id', 'access_token'] },
    { id: 'twitter', name: 'Twitter/X', description: 'Social media engagement', color: 'gray', fields: ['handle', 'api_key', 'api_secret'] },
    { id: 'mailchimp', name: 'Mailchimp', description: 'Email marketing campaigns', color: 'yellow', fields: ['api_key', 'list_id'] },
    { id: 'hubspot', name: 'HubSpot', description: 'CRM and marketing automation', color: 'orange', fields: ['api_key', 'portal_id'] },
    { id: 'semrush', name: 'SEMrush', description: 'SEO and competitor analysis', color: 'orange', fields: ['api_key'] },
    { id: 'ahrefs', name: 'Ahrefs', description: 'Backlink and SEO tools', color: 'blue', fields: ['api_key'] }
  ];

  const handleConnect = async (platform) => {
    // Handle OAuth-based platforms (like TikTok via Outstand)
    if (platform.oauth) {
      try {
        const response = await fetch(`${API_URL}/api/outstand/auth/${platform.id}/connect`);
        const data = await response.json();
        
        if (data.success && data.auth_url) {
          // Redirect to OAuth provider
          window.location.href = data.auth_url;
        } else {
          toast.error(data.error || `Failed to connect ${platform.name}`);
        }
      } catch (error) {
        toast.error(`Failed to connect ${platform.name}`);
        console.error('OAuth connect error:', error);
      }
      return;
    }
    
    // Handle credential-based platforms
    setSelectedPlatform(platform);
    setCredentials({});
    setShowConnectModal(true);
  };

  const handleSaveConnection = () => {
    if (!selectedPlatform) return;
    
    // Validate required fields
    const missingFields = selectedPlatform.fields.filter(f => !credentials[f]?.trim());
    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields`);
      return;
    }

    onConnect(selectedPlatform.id, credentials);
    setShowConnectModal(false);
    setSelectedPlatform(null);
    setCredentials({});
    toast.success(`${selectedPlatform.name} connected successfully!`);
  };

  const connectedCount = connections.filter(c => c.connected).length;

  return (
    <>
      {/* Collapsible Panel Header */}
      <div className="bg-black border border-gray-400 rounded-xl mb-6 overflow-hidden">
        <button
          onClick={onToggle}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-900 transition-colors"
          data-testid="connections-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-500/20 rounded-lg">
              <Link className="text-cyan-400" size={20} />
            </div>
            <div className="text-left">
              <h3 className="text-white font-semibold">Connected Platforms</h3>
              <p className="text-sm text-gray-400">{connectedCount} of {platforms.length} platforms connected</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex -space-x-2">
              {connections.filter(c => c.connected).slice(0, 5).map((conn, idx) => (
                <div key={idx} className="w-8 h-8 rounded-full bg-green-500/20 border-2 border-black flex items-center justify-center">
                  <PlatformIcon platform={conn.platform} size={14} />
                </div>
              ))}
              {connectedCount > 5 && (
                <div className="w-8 h-8 rounded-full bg-gray-700 border-2 border-black flex items-center justify-center text-xs text-white">
                  +{connectedCount - 5}
                </div>
              )}
            </div>
            {isExpanded ? <ChevronUp className="text-gray-400" size={20} /> : <ChevronDown className="text-gray-400" size={20} />}
          </div>
        </button>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="px-6 pb-6 border-t border-gray-600">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 mt-4">
              {platforms.map(platform => {
                const connection = connections.find(c => c.platform === platform.id);
                const isConnected = connection?.connected;

                return (
                  <div
                    key={platform.id}
                    className={`p-4 rounded-xl border transition-all duration-200 ${
                      isConnected 
                        ? 'bg-green-500/10 border-green-500/30 hover:border-green-500/50' 
                        : 'bg-black border-gray-600 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className={`p-2 rounded-lg ${isConnected ? 'bg-green-500/20' : 'bg-gray-700'}`}>
                        <PlatformIcon platform={platform.id} size={18} />
                      </div>
                      {isConnected && <CheckCircle className="text-green-400" size={16} />}
                    </div>
                    <h4 className="text-white font-medium text-sm">{platform.name}</h4>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{platform.description}</p>
                    <button
                      onClick={() => isConnected ? onDisconnect(platform.id) : handleConnect(platform)}
                      className={`w-full mt-3 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        isConnected
                          ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                          : 'bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30'
                      }`}
                      data-testid={`connect-${platform.id}`}
                    >
                      {isConnected ? 'Disconnect' : 'Connect'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Connection Modal */}
      {showConnectModal && selectedPlatform && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowConnectModal(false)}>
          <div className="bg-black border border-gray-400 rounded-xl w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="connect-modal">
            <div className="p-6 border-b border-gray-600">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-xl bg-${selectedPlatform.color}-500/20`}>
                  <PlatformIcon platform={selectedPlatform.id} size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Connect {selectedPlatform.name}</h3>
                  <p className="text-sm text-gray-400">{selectedPlatform.description}</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {selectedPlatform.fields.map(field => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-300 mb-1 capitalize">
                    {field.replace(/_/g, ' ')} *
                  </label>
                  <input
                    type={field.includes('key') || field.includes('token') || field.includes('secret') ? 'password' : 'text'}
                    value={credentials[field] || ''}
                    onChange={e => setCredentials({...credentials, [field]: e.target.value})}
                    className="w-full px-4 py-2 bg-black border border-gray-500 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                    placeholder={`Enter ${field.replace(/_/g, ' ')}...`}
                  />
                </div>
              ))}

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <p className="text-xs text-blue-400">
                  <strong>Note:</strong> Your credentials are encrypted and stored securely. We only use them to fetch your marketing data.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-600 flex gap-3">
              <button
                onClick={() => setShowConnectModal(false)}
                className="flex-1 px-4 py-3 bg-black border border-gray-500 hover:bg-gray-900 text-gray-300 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveConnection}
                className="flex-1 px-4 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg flex items-center justify-center gap-2 transition-colors"
                data-testid="save-connection-btn"
              >
                <Link size={18} />
                Connect
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ============== AI ASSISTANT PANEL ==============
const AIAssistantPanel = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Generate session ID on mount
  useEffect(() => {
    if (!sessionId) {
      setSessionId(`marketing-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
    }
  }, [sessionId]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage, timestamp: new Date().toISOString() }]);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/marketing/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMessage, session_id: sessionId })
      });

      if (!response.ok) throw new Error('Failed to get AI response');

      const data = await response.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.message, timestamp: new Date().toISOString() }]);
    } catch (error) {
      toast.error('Failed to get AI response');
      console.error('AI Chat error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setSessionId(`marketing-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  };

  const quickPrompts = [
    "Analyze my top traffic sources",
    "Suggest SEO improvements",
    "How to increase leads?",
    "Content strategy ideas"
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-y-0 right-0 w-96 bg-black border-l border-gray-400 shadow-2xl z-50 flex flex-col" data-testid="ai-assistant-panel">
      {/* Header */}
      <div className="p-4 border-b border-gray-600 flex items-center justify-between bg-gradient-to-r from-purple-900/50 to-black">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <Bot className="text-purple-400" size={20} />
          </div>
          <div>
            <h3 className="text-white font-semibold">AI Marketing Assistant</h3>
            <p className="text-xs text-gray-400">Powered by GPT-5.2</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={clearChat} className="p-1.5 hover:bg-gray-700 rounded text-gray-400" title="Clear chat">
            <RefreshCw size={16} />
          </button>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-700 rounded text-gray-400" data-testid="close-ai-panel">
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <Sparkles className="mx-auto text-purple-400 mb-4" size={40} />
            <h4 className="text-white font-medium mb-2">How can I help?</h4>
            <p className="text-gray-400 text-sm mb-6">Ask me anything about marketing strategy, SEO, campaigns, or content.</p>
            <div className="space-y-2">
              {quickPrompts.map((prompt, idx) => (
                <button
                  key={idx}
                  onClick={() => setInputMessage(prompt)}
                  className="w-full text-left px-3 py-2 bg-black border border-gray-600 hover:bg-gray-900 hover:border-gray-400 rounded-lg text-sm text-gray-300 transition-all duration-200"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] p-3 rounded-xl ${
                msg.role === 'user' 
                  ? 'bg-cyan-600 text-white' 
                  : 'bg-black border border-gray-600 text-gray-200'
              }`}>
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))
        )}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-black border border-gray-600 p-3 rounded-xl flex items-center gap-2">
              <Loader2 className="animate-spin text-purple-400" size={16} />
              <span className="text-sm text-gray-400">Thinking...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-600">
        <div className="flex gap-2">
          <textarea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about marketing..."
            className="flex-1 px-4 py-3 bg-black border border-gray-500 rounded-xl text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none resize-none text-sm"
            rows={2}
            data-testid="ai-chat-input"
          />
          <button
            onClick={sendMessage}
            disabled={!inputMessage.trim() || isLoading}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-xl transition-colors"
            data-testid="ai-send-btn"
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

// ============== SEO BLOG CREATION MODAL ==============
const SEOBlogModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    target_keyword: '',
    content: '',
    meta_description: '',
    is_hidden_seo: true
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationSettings, setGenerationSettings] = useState({
    tone: 'professional',
    word_count: 1000
  });

  const generateWithAI = async () => {
    if (!formData.target_keyword && !formData.title) {
      toast.error('Please enter a title or target keyword first');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/marketing/ai/generate-content`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: formData.title,
          target_keyword: formData.target_keyword,
          content_type: 'blog',
          tone: generationSettings.tone,
          word_count: generationSettings.word_count
        })
      });

      if (!response.ok) throw new Error('Generation failed');

      const data = await response.json();
      setFormData(prev => ({
        ...prev,
        title: data.generated_title || prev.title,
        meta_description: data.meta_description || prev.meta_description,
        content: data.content || prev.content
      }));
      toast.success('Content generated successfully!');
    } catch (error) {
      toast.error('Failed to generate content');
      console.error('Generation error:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSubmit = () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    onSave(formData);
    setFormData({ title: '', target_keyword: '', content: '', meta_description: '', is_hidden_seo: true });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-black border border-gray-400 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden hover:border-white transition-all duration-200" onClick={e => e.stopPropagation()} data-testid="seo-blog-modal">
        <div className="p-6 border-b border-gray-600 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <FileText className="text-orange-400" size={24} />
              Create Hidden SEO Blog Post
            </h3>
            <p className="text-sm text-gray-400 mt-1">Create content optimized for search engines</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-700 rounded-lg text-gray-400">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={e => setFormData({...formData, title: e.target.value})}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                placeholder="Enter blog title..."
                data-testid="seo-blog-title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Target Keyword</label>
              <input
                type="text"
                value={formData.target_keyword}
                onChange={e => setFormData({...formData, target_keyword: e.target.value})}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                placeholder="e.g., commercial real estate"
                data-testid="seo-blog-keyword"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Meta Description</label>
            <input
              type="text"
              value={formData.meta_description}
              onChange={e => setFormData({...formData, meta_description: e.target.value})}
              className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
              placeholder="150-160 characters for search results..."
              maxLength={160}
            />
            <p className="text-xs text-gray-500 mt-1">{formData.meta_description.length}/160 characters</p>
          </div>

          {/* AI Generation Section */}
          <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-purple-400 font-medium flex items-center gap-2">
                <Wand2 size={16} />
                AI Content Generation
              </h4>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Tone</label>
                <select
                  value={generationSettings.tone}
                  onChange={e => setGenerationSettings({...generationSettings, tone: e.target.value})}
                  className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm"
                >
                  <option value="professional">Professional</option>
                  <option value="conversational">Conversational</option>
                  <option value="authoritative">Authoritative</option>
                  <option value="friendly">Friendly</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Word Count</label>
                <select
                  value={generationSettings.word_count}
                  onChange={e => setGenerationSettings({...generationSettings, word_count: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm"
                >
                  <option value={500}>~500 words</option>
                  <option value={1000}>~1000 words</option>
                  <option value={1500}>~1500 words</option>
                  <option value={2000}>~2000 words</option>
                </select>
              </div>
            </div>
            <button
              onClick={generateWithAI}
              disabled={isGenerating || (!formData.title && !formData.target_keyword)}
              className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg flex items-center justify-center gap-2 transition-colors"
              data-testid="generate-content-btn"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles size={16} />
                  Generate Content with AI
                </>
              )}
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Content</label>
            <textarea
              value={formData.content}
              onChange={e => setFormData({...formData, content: e.target.value})}
              className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none font-mono text-sm"
              rows={12}
              placeholder="Write or generate your blog content..."
              data-testid="seo-blog-content"
            />
            <p className="text-xs text-gray-500 mt-1">{formData.content.split(/\s+/).filter(Boolean).length} words</p>
          </div>

          <div className="flex items-center gap-3 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
            <input
              type="checkbox"
              id="hiddenSeo"
              checked={formData.is_hidden_seo}
              onChange={e => setFormData({...formData, is_hidden_seo: e.target.checked})}
              className="w-4 h-4 rounded bg-black border-gray-500"
            />
            <label htmlFor="hiddenSeo" className="flex-1">
              <span className="text-orange-400 font-medium flex items-center gap-2">
                <EyeOff size={16} />
                Hidden SEO Post
              </span>
              <span className="text-xs text-gray-400 block mt-0.5">
                This content will be indexed by search engines but not shown on your main site
              </span>
            </label>
          </div>
        </div>

        <div className="p-6 border-t border-gray-600 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-black border border-gray-500 hover:bg-gray-900 text-gray-300 rounded-lg transition-all duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 px-4 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg flex items-center justify-center gap-2"
            data-testid="save-seo-blog-btn"
          >
            <FileText size={18} />
            Create Blog Post
          </button>
        </div>
      </div>
    </div>
  );
};

// ============== CONTENT MODAL ==============
const ContentModal = ({ isOpen, onClose, onSave, content = null }) => {
  const [formData, setFormData] = useState({
    title: content?.title || '',
    type: content?.type || 'blog',
    status: content?.status || 'draft',
    description: content?.description || '',
    url: content?.url || ''
  });

  useEffect(() => {
    if (content) {
      setFormData({
        title: content.title || '',
        type: content.type || 'blog',
        status: content.status || 'draft',
        description: content.description || '',
        url: content.url || ''
      });
    } else {
      setFormData({
        title: '',
        type: 'blog',
        status: 'draft',
        description: '',
        url: ''
      });
    }
  }, [content]);

  const handleSubmit = () => {
    if (!formData.title.trim()) {
      return;
    }
    onSave(formData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-black border border-gray-400 rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden hover:border-white transition-all duration-200" onClick={e => e.stopPropagation()} data-testid="content-modal">
        <div className="p-6 border-b border-gray-600 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <FileText className="text-cyan-400" size={24} />
              {content ? 'Edit Content' : 'New Content'}
            </h3>
            <p className="text-sm text-gray-400 mt-1">Create or edit marketing content</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-900 rounded-lg text-gray-400 transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={e => setFormData({...formData, title: e.target.value})}
              className="w-full px-4 py-2 bg-black border border-gray-500 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
              placeholder="Enter content title..."
              data-testid="content-title-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Type</label>
              <select
                value={formData.type}
                onChange={e => setFormData({...formData, type: e.target.value})}
                className="w-full px-4 py-2 bg-black border border-gray-500 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                data-testid="content-type-select"
              >
                <option value="blog">Blog Post</option>
                <option value="landing_page">Landing Page</option>
                <option value="case_study">Case Study</option>
                <option value="whitepaper">Whitepaper</option>
                <option value="video">Video</option>
                <option value="infographic">Infographic</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
              <select
                value={formData.status}
                onChange={e => setFormData({...formData, status: e.target.value})}
                className="w-full px-4 py-2 bg-black border border-gray-500 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                data-testid="content-status-select"
              >
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({...formData, description: e.target.value})}
              className="w-full px-4 py-2 bg-black border border-gray-500 rounded-lg text-white focus:border-cyan-500 focus:outline-none resize-none"
              placeholder="Brief description of this content..."
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">URL (optional)</label>
            <input
              type="text"
              value={formData.url}
              onChange={e => setFormData({...formData, url: e.target.value})}
              className="w-full px-4 py-2 bg-black border border-gray-500 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
              placeholder="https://..."
            />
          </div>
        </div>

        <div className="p-6 border-t border-gray-600 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-black border border-gray-500 hover:bg-gray-900 hover:border-gray-400 text-gray-300 rounded-lg transition-all duration-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!formData.title.trim()}
            className="flex-1 px-4 py-3 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg flex items-center justify-center gap-2 transition-colors"
            data-testid="save-content-btn"
          >
            <FileText size={18} />
            {content ? 'Save Changes' : 'Create Content'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Tab Configuration
const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'campaigns', label: 'Campaigns', icon: Megaphone },
  { id: 'analytics', label: 'Traffic & Analytics', icon: Activity },
  { id: 'seo', label: 'SEO Hub', icon: Search },
  { id: 'content', label: 'Content', icon: FileText },
  { id: 'brand', label: 'Brand Presence', icon: Eye },
  { id: 'attribution', label: 'Lead Attribution', icon: Target },
  { id: 'services', label: 'Buy Services', icon: ShoppingCart },
  { id: 'reports', label: 'Reports', icon: Download }
];

// Metric Card Component
const MetricCard = ({ title, value, change, icon: Icon, color = "cyan", subtitle }) => (
  <div className="bg-black border border-gray-400 rounded-xl p-4 hover:bg-gray-900 hover:border-white transition-all duration-200">
    <div className="flex items-start justify-between mb-2">
      <div className={`p-2 rounded-lg bg-${color}-500/20`}>
        <Icon className={`text-${color}-400`} size={20} />
      </div>
      {change !== undefined && (
        <div className={`flex items-center gap-1 text-sm ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {change >= 0 ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
          {Math.abs(change)}%
        </div>
      )}
    </div>
    <div className="text-2xl font-bold text-white">{value}</div>
    <div className="text-sm text-gray-400">{title}</div>
    {subtitle && <div className="text-xs text-gray-500 mt-1">{subtitle}</div>}
  </div>
);

// Status Badge Component
const StatusBadge = ({ status }) => {
  const colors = {
    active: 'bg-green-500/20 text-green-400',
    planned: 'bg-blue-500/20 text-blue-400',
    paused: 'bg-yellow-500/20 text-yellow-400',
    completed: 'bg-gray-500/20 text-gray-400',
    published: 'bg-green-500/20 text-green-400',
    draft: 'bg-gray-500/20 text-gray-400',
    scheduled: 'bg-purple-500/20 text-purple-400',
    hidden: 'bg-orange-500/20 text-orange-400',
    strong: 'bg-green-500/20 text-green-400',
    fair: 'bg-yellow-500/20 text-yellow-400',
    needs_work: 'bg-red-500/20 text-red-400',
    average: 'bg-yellow-500/20 text-yellow-400'
  };
  
  return (
    <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${colors[status] || 'bg-gray-500/20 text-gray-400'}`}>
      {status?.replace('_', ' ')}
    </span>
  );
};

// AI Insight Card
const InsightCard = ({ insight }) => {
  const icons = {
    opportunity: Lightbulb,
    improvement: TrendingUp,
    alert: AlertCircle,
    suggestion: Sparkles,
    positive: CheckCircle,
    negative: AlertCircle
  };
  const colors = {
    opportunity: 'text-yellow-400 bg-yellow-500/20',
    improvement: 'text-blue-400 bg-blue-500/20',
    alert: 'text-red-400 bg-red-500/20',
    suggestion: 'text-purple-400 bg-purple-500/20',
    positive: 'text-green-400 bg-green-500/20',
    negative: 'text-red-400 bg-red-500/20'
  };
  
  const Icon = icons[insight.type] || Lightbulb;
  const colorClass = colors[insight.type] || 'text-gray-400 bg-gray-500/20';
  
  return (
    <div className="flex items-start gap-3 p-3 bg-black border border-gray-600 rounded-lg hover:border-gray-400 transition-all duration-200">
      <div className={`p-2 rounded-lg ${colorClass.split(' ')[1]}`}>
        <Icon className={colorClass.split(' ')[0]} size={16} />
      </div>
      <div className="flex-1">
        <p className="text-sm text-gray-300">{insight.message}</p>
        {insight.priority && (
          <span className={`text-xs mt-1 inline-block ${
            insight.priority === 'high' ? 'text-red-400' : 
            insight.priority === 'medium' ? 'text-yellow-400' : 'text-gray-500'
          }`}>
            {insight.priority} priority
          </span>
        )}
      </div>
    </div>
  );
};

const MarketingHub = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [dashboard, setDashboard] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [seoData, setSeoData] = useState(null);
  const [content, setContent] = useState([]);
  const [brandPresence, setBrandPresence] = useState(null);
  const [attribution, setAttribution] = useState(null);
  const [services, setServices] = useState([]);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [monthlyReport, setMonthlyReport] = useState(null);
  
  // Modal states
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showContentModal, setShowContentModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [showSEOBlogModal, setShowSEOBlogModal] = useState(false);
  
  // Stripe checkout states
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  // Connections panel state
  const [connectionsExpanded, setConnectionsExpanded] = useState(false);
  const [connections, setConnections] = useState(() => {
    // Load from localStorage
    const saved = localStorage.getItem('marketing_connections');
    return saved ? JSON.parse(saved) : [];
  });

  // Save connections to localStorage
  useEffect(() => {
    localStorage.setItem('marketing_connections', JSON.stringify(connections));
  }, [connections]);

  // Handle connecting a platform
  const handleConnectPlatform = (platformId, credentials) => {
    setConnections(prev => {
      const existing = prev.find(c => c.platform === platformId);
      if (existing) {
        return prev.map(c => c.platform === platformId 
          ? { ...c, connected: true, credentials, connectedAt: new Date().toISOString() }
          : c
        );
      }
      return [...prev, { 
        platform: platformId, 
        connected: true, 
        credentials, 
        connectedAt: new Date().toISOString() 
      }];
    });
  };

  // Handle disconnecting a platform
  const handleDisconnectPlatform = (platformId) => {
    if (!window.confirm('Are you sure you want to disconnect this platform?')) return;
    setConnections(prev => prev.map(c => 
      c.platform === platformId ? { ...c, connected: false, credentials: null } : c
    ));
    toast.success('Platform disconnected');
  };

  // Check for payment return from Stripe
  useEffect(() => {
    const paymentParam = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (paymentParam === 'success' && sessionId) {
      setActiveTab('services');
      pollPaymentStatus(sessionId);
      // Clean URL
      setSearchParams({});
    } else if (paymentParam === 'cancelled') {
      toast.error('Payment was cancelled');
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Poll for payment status
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus({ status: 'timeout', message: 'Payment status check timed out. Please check your email for confirmation.' });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/payments/checkout/status/${sessionId}`);
      if (!response.ok) throw new Error('Failed to check status');

      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        setPaymentStatus({ status: 'success', message: 'Payment successful! We\'ll be in touch soon to get started.' });
        toast.success('Payment successful! Thank you for your purchase.');
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus({ status: 'expired', message: 'Payment session expired. Please try again.' });
        return;
      }

      // Continue polling
      setPaymentStatus({ status: 'pending', message: 'Processing payment...' });
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus({ status: 'error', message: 'Error checking payment status.' });
    }
  };

  // Stripe checkout handler
  const handleStripeCheckout = async (serviceId) => {
    setIsCheckingOut(true);
    try {
      const response = await fetch(`${API_URL}/api/payments/checkout/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_id: serviceId,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) throw new Error('Failed to create checkout session');

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      toast.error('Failed to start checkout. Please try again.');
      console.error('Checkout error:', error);
    } finally {
      setIsCheckingOut(false);
    }
  };

  // Create SEO Blog handler
  const handleCreateSEOBlog = async (blogData) => {
    try {
      const response = await fetch(`${API_URL}/api/marketing/content/seo-blog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(blogData)
      });

      if (!response.ok) throw new Error('Failed to create blog');

      toast.success('SEO Blog post created successfully!');
      fetchContent();
    } catch (error) {
      toast.error('Failed to create blog post');
      console.error('Create blog error:', error);
    }
  };

  // Create or update content
  const handleSaveContent = async (contentData) => {
    try {
      const response = await fetch(`${API_URL}/api/marketing/content`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contentData)
      });

      if (!response.ok) throw new Error('Failed to create content');

      toast.success('Content created successfully!');
      fetchContent();
      setShowContentModal(false);
      setSelectedContent(null);
    } catch (error) {
      toast.error('Failed to create content');
      console.error('Create content error:', error);
    }
  };

  // Fetch functions
  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/marketing/dashboard`);
      const data = await res.json();
      setDashboard(data);
    } catch (e) { console.error('Failed to fetch dashboard:', e); }
  }, []);

  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/marketing/campaigns`);
      const data = await res.json();
      setCampaigns(data.campaigns || []);
    } catch (e) { console.error('Failed to fetch campaigns:', e); }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/marketing/analytics`);
      const data = await res.json();
      setAnalytics(data);
    } catch (e) { console.error('Failed to fetch analytics:', e); }
  }, []);

  const fetchSEO = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/marketing/seo`);
      const data = await res.json();
      setSeoData(data);
    } catch (e) { console.error('Failed to fetch SEO data:', e); }
  }, []);

  const fetchContent = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/marketing/content`);
      const data = await res.json();
      setContent(data.content || []);
    } catch (e) { console.error('Failed to fetch content:', e); }
  }, []);

  const fetchBrandPresence = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/marketing/brand-presence`);
      const data = await res.json();
      setBrandPresence(data);
    } catch (e) { console.error('Failed to fetch brand presence:', e); }
  }, []);

  const fetchAttribution = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/marketing/attribution`);
      const data = await res.json();
      setAttribution(data);
    } catch (e) { console.error('Failed to fetch attribution:', e); }
  }, []);

  const fetchServices = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/marketing/services`);
      const data = await res.json();
      setServices(data.services || []);
    } catch (e) { console.error('Failed to fetch services:', e); }
  }, []);

  const fetchReports = useCallback(async () => {
    try {
      const [weeklyRes, monthlyRes] = await Promise.all([
        fetch(`${API_URL}/api/marketing/reports/weekly`),
        fetch(`${API_URL}/api/marketing/reports/monthly`)
      ]);
      setWeeklyReport(await weeklyRes.json());
      setMonthlyReport(await monthlyRes.json());
    } catch (e) { console.error('Failed to fetch reports:', e); }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(),
        fetchCampaigns(),
        fetchAnalytics(),
        fetchSEO(),
        fetchContent(),
        fetchBrandPresence(),
        fetchAttribution(),
        fetchServices(),
        fetchReports()
      ]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Campaign handlers
  const handleCreateCampaign = async (formData) => {
    try {
      const res = await fetch(`${API_URL}/api/marketing/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        toast.success('Campaign created!');
        fetchCampaigns();
        setShowCampaignModal(false);
      }
    } catch (e) {
      toast.error('Failed to create campaign');
    }
  };

  const handleUpdateCampaignStatus = async (campaignId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/marketing/campaigns/${campaignId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        toast.success('Campaign updated!');
        fetchCampaigns();
      }
    } catch (e) {
      toast.error('Failed to update campaign');
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Delete this campaign?')) return;
    try {
      const res = await fetch(`${API_URL}/api/marketing/campaigns/${campaignId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success('Campaign deleted');
        fetchCampaigns();
      }
    } catch (e) {
      toast.error('Failed to delete campaign');
    }
  };

  // Service order handler
  const handleOrderService = async (serviceId) => {
    try {
      const res = await fetch(`${API_URL}/api/marketing/services/order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ service_id: serviceId })
      });
      if (res.ok) {
        toast.success('Service ordered! We\'ll be in touch soon.');
        setShowServiceModal(false);
      }
    } catch (e) {
      toast.error('Failed to order service');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="w-8 h-8 text-cyan-400 animate-spin" />
          <p className="text-gray-400">Loading Marketing Hub...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="border-b border-gray-600 bg-black/80 sticky top-0 z-40">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Megaphone className="text-cyan-400" size={28} />
                Marketing Hub
              </h1>
              <p className="text-gray-400 text-sm mt-1">Track campaigns, analytics, SEO, and grow your business</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowAIPanel(!showAIPanel)}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-colors"
              >
                <Bot size={18} />
                AI Assistant
              </button>
              <button
                onClick={() => setShowCampaignModal(true)}
                className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg flex items-center gap-2 transition-colors"
              >
                <Plus size={18} />
                New Campaign
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-1 mt-4 overflow-x-auto pb-2">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 whitespace-nowrap transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/50'
                    : 'text-gray-400 hover:text-white hover:bg-black hover:border hover:border-gray-500'
                }`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {/* Connections Panel - Always visible at top */}
        <ConnectionsPanel
          connections={connections}
          onConnect={handleConnectPlatform}
          onDisconnect={handleDisconnectPlatform}
          isExpanded={connectionsExpanded}
          onToggle={() => setConnectionsExpanded(!connectionsExpanded)}
        />

        {/* Overview Tab */}
        {activeTab === 'overview' && dashboard && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              <MetricCard
                title="Active Campaigns"
                value={dashboard.overview.active_campaigns}
                icon={Megaphone}
                subtitle={`of ${dashboard.overview.total_campaigns} total`}
              />
              <MetricCard
                title="Website Traffic"
                value={dashboard.overview.website_traffic_7d.toLocaleString()}
                change={dashboard.overview.traffic_change_percent}
                icon={Globe}
                subtitle="Last 7 days"
              />
              <MetricCard
                title="Traffic (30d)"
                value={dashboard.overview.website_traffic_30d.toLocaleString()}
                icon={Activity}
              />
              <MetricCard
                title="SEO Health"
                value={dashboard.overview.seo_health_score}
                icon={Search}
                subtitle={dashboard.overview.seo_health_label}
                color={dashboard.overview.seo_health_label === 'Strong' ? 'green' : dashboard.overview.seo_health_label === 'Fair' ? 'yellow' : 'red'}
              />
              <MetricCard
                title="Leads This Month"
                value={dashboard.overview.leads_this_month}
                change={dashboard.overview.leads_change_percent}
                icon={Users}
              />
              <MetricCard
                title="Content Published"
                value={dashboard.overview.content_published_month}
                icon={FileText}
                subtitle="This month"
              />
              <MetricCard
                title="Opportunities"
                value={dashboard.overview.opportunities_detected}
                icon={Lightbulb}
                color="yellow"
                subtitle="AI detected"
              />
            </div>

            {/* Traffic Sources & Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Traffic Sources */}
              <div className="bg-black border border-gray-400 rounded-xl p-6 hover:border-white transition-all duration-200">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <PieChart className="text-cyan-400" size={20} />
                  Top Traffic Sources
                </h3>
                <div className="space-y-3">
                  {dashboard.traffic_sources.map((source, idx) => (
                    <div key={idx} className="flex items-center gap-4">
                      <div className="w-24 text-sm text-gray-400">{source.source}</div>
                      <div className="flex-1 bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-cyan-500 to-blue-500 h-2 rounded-full"
                          style={{ width: `${source.percent}%` }}
                        />
                      </div>
                      <div className="w-16 text-sm text-white text-right">{source.visits.toLocaleString()}</div>
                      <div className={`w-12 text-sm text-right ${source.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {source.change >= 0 ? '+' : ''}{source.change}%
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Activity */}
              <div className="bg-black border border-gray-400 rounded-xl p-6 hover:border-white transition-all duration-200">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Clock className="text-cyan-400" size={20} />
                  Recent Activity
                </h3>
                <div className="space-y-3">
                  {dashboard.recent_activity.map((activity, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-black border border-gray-600 rounded-lg hover:border-gray-400 transition-all duration-200">
                      <div className={`p-2 rounded-lg ${
                        activity.type === 'campaign' ? 'bg-blue-500/20' :
                        activity.type === 'seo' ? 'bg-green-500/20' :
                        activity.type === 'content' ? 'bg-purple-500/20' : 'bg-yellow-500/20'
                      }`}>
                        {activity.type === 'campaign' ? <Megaphone size={14} className="text-blue-400" /> :
                         activity.type === 'seo' ? <Search size={14} className="text-green-400" /> :
                         activity.type === 'content' ? <FileText size={14} className="text-purple-400" /> :
                         <Users size={14} className="text-yellow-400" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-300">{activity.message}</p>
                        <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* AI Insights */}
            <div className="bg-black border border-gray-400 rounded-xl p-6 hover:border-white transition-all duration-200">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Sparkles className="text-purple-400" size={20} />
                AI Marketing Insights
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {dashboard.ai_insights.map((insight, idx) => (
                  <InsightCard key={idx} insight={insight} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Campaigns Tab */}
        {activeTab === 'campaigns' && (
          <div className="space-y-6">
            {/* Campaign Stats */}
            <div className="grid grid-cols-4 gap-4">
              <MetricCard title="Active" value={campaigns.filter(c => c.status === 'active').length} icon={Play} color="green" />
              <MetricCard title="Planned" value={campaigns.filter(c => c.status === 'planned').length} icon={Calendar} color="blue" />
              <MetricCard title="Paused" value={campaigns.filter(c => c.status === 'paused').length} icon={Pause} color="yellow" />
              <MetricCard title="Completed" value={campaigns.filter(c => c.status === 'completed').length} icon={CheckCircle} color="gray" />
            </div>

            {/* Campaigns List */}
            <div className="bg-black border border-gray-400 rounded-xl overflow-hidden hover:border-white transition-all duration-200">
              <div className="p-4 border-b border-gray-600 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">All Campaigns</h3>
                <button
                  onClick={() => { setSelectedCampaign(null); setShowCampaignModal(true); }}
                  className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-sm rounded-lg flex items-center gap-1"
                >
                  <Plus size={14} /> New Campaign
                </button>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-600 hover:bg-gray-900">
                    <th className="text-left px-4 py-3 text-sm text-gray-400 font-medium">Campaign</th>
                    <th className="text-left px-4 py-3 text-sm text-gray-400 font-medium">Type</th>
                    <th className="text-left px-4 py-3 text-sm text-gray-400 font-medium">Status</th>
                    <th className="text-left px-4 py-3 text-sm text-gray-400 font-medium">Goal</th>
                    <th className="text-left px-4 py-3 text-sm text-gray-400 font-medium">Budget</th>
                    <th className="text-left px-4 py-3 text-sm text-gray-400 font-medium">Results</th>
                    <th className="text-right px-4 py-3 text-sm text-gray-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map(campaign => (
                    <tr key={campaign.id} className="border-b border-gray-600 hover:hover:bg-gray-900">
                      <td className="px-4 py-3">
                        <div className="text-white font-medium">{campaign.name}</div>
                        <div className="text-xs text-gray-500">{campaign.owner}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-300 capitalize">{campaign.type?.replace('_', ' ')}</span>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={campaign.status} />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-300 capitalize">{campaign.goal}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-300">
                          {campaign.budget ? `$${campaign.budget.toLocaleString()}` : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs text-gray-400">
                          {campaign.results?.leads ? `${campaign.results.leads} leads` : 
                           campaign.results?.impressions ? `${campaign.results.impressions.toLocaleString()} impr.` : '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {campaign.status === 'active' && (
                            <button
                              onClick={() => handleUpdateCampaignStatus(campaign.id, 'paused')}
                              className="p-1.5 hover:bg-gray-700 rounded text-yellow-400"
                              title="Pause"
                            >
                              <Pause size={14} />
                            </button>
                          )}
                          {campaign.status === 'paused' && (
                            <button
                              onClick={() => handleUpdateCampaignStatus(campaign.id, 'active')}
                              className="p-1.5 hover:bg-gray-700 rounded text-green-400"
                              title="Resume"
                            >
                              <Play size={14} />
                            </button>
                          )}
                          <button
                            onClick={() => { setSelectedCampaign(campaign); setShowCampaignModal(true); }}
                            className="p-1.5 hover:bg-gray-700 rounded text-gray-400"
                            title="Edit"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteCampaign(campaign.id)}
                            className="p-1.5 hover:bg-gray-700 rounded text-red-400"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <MetricCard title="Total Visitors" value={analytics.summary.total_visitors.toLocaleString()} icon={Users} />
              <MetricCard title="Page Views" value={analytics.summary.total_pageviews.toLocaleString()} icon={Eye} />
              <MetricCard title="Avg. Time on Site" value={analytics.summary.avg_time_on_site} icon={Clock} />
              <MetricCard title="Bounce Rate" value={`${analytics.summary.bounce_rate}%`} icon={TrendingDown} color="yellow" />
              <MetricCard title="Conversion Rate" value={`${analytics.summary.conversion_rate}%`} icon={Target} color="green" />
              <MetricCard title="Conversions" value={analytics.summary.conversions} icon={CheckCircle} color="green" />
            </div>

            {/* Top Pages & Device Split */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Top Pages */}
              <div className="lg:col-span-2 bg-black border border-gray-400 rounded-xl p-6 hover:border-white transition-all duration-200">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <FileText className="text-cyan-400" size={20} />
                  Top Pages
                </h3>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-600">
                      <th className="text-left py-2 text-sm text-gray-400">Page</th>
                      <th className="text-right py-2 text-sm text-gray-400">Views</th>
                      <th className="text-right py-2 text-sm text-gray-400">Avg. Time</th>
                      <th className="text-right py-2 text-sm text-gray-400">Conversions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.top_pages.map((page, idx) => (
                      <tr key={idx} className="border-b border-gray-600">
                        <td className="py-3">
                          <div className="text-white text-sm">{page.title}</div>
                          <div className="text-xs text-gray-500">{page.page}</div>
                        </td>
                        <td className="text-right text-sm text-gray-300">{page.views.toLocaleString()}</td>
                        <td className="text-right text-sm text-gray-300">{page.avg_time}</td>
                        <td className="text-right text-sm text-green-400">{page.conversions}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Device Split & Locations */}
              <div className="space-y-6">
                <div className="bg-black border border-gray-400 rounded-xl p-6 hover:border-white transition-all duration-200">
                  <h3 className="text-lg font-semibold text-white mb-4">Device Split</h3>
                  <div className="space-y-3">
                    {Object.entries(analytics.device_split).map(([device, percent]) => (
                      <div key={device} className="flex items-center gap-3">
                        <div className="w-16 text-sm text-gray-400 capitalize">{device}</div>
                        <div className="flex-1 bg-gray-700 rounded-full h-2">
                          <div 
                            className="bg-cyan-500 h-2 rounded-full"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                        <div className="w-10 text-sm text-white text-right">{percent}%</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-black border border-gray-400 rounded-xl p-6 hover:border-white transition-all duration-200">
                  <h3 className="text-lg font-semibold text-white mb-4">Top Locations</h3>
                  <div className="space-y-2">
                    {analytics.top_locations.slice(0, 4).map((loc, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="text-gray-300">{loc.location}</span>
                        <span className="text-gray-400">{loc.percent}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* AI Insights */}
            <div className="bg-black border border-gray-400 rounded-xl p-6 hover:border-white transition-all duration-200">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Sparkles className="text-purple-400" size={20} />
                Traffic Insights
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {analytics.ai_insights.map((insight, idx) => (
                  <InsightCard key={idx} insight={insight} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* SEO Tab */}
        {activeTab === 'seo' && seoData && (
          <div className="space-y-6">
            {/* SEO Health */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-black border border-gray-400 rounded-xl p-6 hover:border-white transition-all duration-200 text-center">
                <div className={`text-4xl font-bold ${
                  seoData.health_score >= 70 ? 'text-green-400' : 
                  seoData.health_score >= 50 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {seoData.health_score}
                </div>
                <div className="text-gray-400 text-sm mt-1">SEO Health Score</div>
                <StatusBadge status={seoData.health_label.toLowerCase()} />
              </div>
              <MetricCard title="Keywords Ranking" value={seoData.keywords_ranking.length} icon={Search} />
              <MetricCard title="Indexed Pages" value={`${seoData.indexed_pages}/${seoData.total_pages}`} icon={FileText} />
              <MetricCard title="Backlinks" value={seoData.backlinks.total} icon={ExternalLink} subtitle={`+${seoData.backlinks.new_this_month} this month`} />
            </div>

            {/* Keywords */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Current Rankings */}
              <div className="bg-black border border-gray-400 rounded-xl p-6 hover:border-white transition-all duration-200">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="text-cyan-400" size={20} />
                  Keyword Rankings
                </h3>
                <div className="space-y-3">
                  {seoData.keywords_ranking.map((kw, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-black border border-gray-600 rounded-lg hover:border-gray-400 transition-all duration-200">
                      <div className="flex-1">
                        <div className="text-white text-sm">{kw.keyword}</div>
                        <div className="text-xs text-gray-500">{kw.volume.toLocaleString()} monthly searches</div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <div className="text-lg font-bold text-white">#{kw.position}</div>
                          <div className={`text-xs ${kw.change > 0 ? 'text-green-400' : kw.change < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                            {kw.change > 0 ? `↑${kw.change}` : kw.change < 0 ? `↓${Math.abs(kw.change)}` : '—'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Opportunities */}
              <div className="bg-black border border-gray-400 rounded-xl p-6 hover:border-white transition-all duration-200">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Lightbulb className="text-yellow-400" size={20} />
                  Keyword Opportunities
                </h3>
                <div className="space-y-3">
                  {seoData.keyword_opportunities.map((kw, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-black border border-gray-600 rounded-lg hover:border-gray-400 transition-all duration-200">
                      <div className="flex-1">
                        <div className="text-white text-sm">{kw.keyword}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-500">{kw.volume.toLocaleString()} vol.</span>
                          <span className="text-xs text-gray-500">Difficulty: {kw.difficulty}</span>
                        </div>
                      </div>
                      <StatusBadge status={kw.potential} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Technical Issues */}
            <div className="bg-black border border-gray-400 rounded-xl p-6 hover:border-white transition-all duration-200">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <AlertCircle className="text-red-400" size={20} />
                Technical Issues to Fix
              </h3>
              <div className="space-y-3">
                {seoData.technical_issues.map((issue, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-4 bg-black border border-gray-600 rounded-lg hover:border-gray-400 transition-all duration-200">
                    <div className={`p-2 rounded-lg ${
                      issue.severity === 'high' ? 'bg-red-500/20' : 
                      issue.severity === 'medium' ? 'bg-yellow-500/20' : 'bg-gray-500/20'
                    }`}>
                      <AlertCircle size={16} className={
                        issue.severity === 'high' ? 'text-red-400' : 
                        issue.severity === 'medium' ? 'text-yellow-400' : 'text-gray-400'
                      } />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{issue.issue}</span>
                        <span className="text-xs text-gray-500">({issue.pages} pages)</span>
                      </div>
                      <p className="text-sm text-gray-400 mt-1">{issue.fix}</p>
                    </div>
                    <button className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-sm rounded-lg">
                      Fix Now
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Content Tab */}
        {activeTab === 'content' && (
          <div className="space-y-6">
            {/* Content Stats */}
            <div className="grid grid-cols-4 gap-4">
              <MetricCard title="Published" value={content.filter(c => c.status === 'published').length} icon={CheckCircle} color="green" />
              <MetricCard title="Drafts" value={content.filter(c => c.status === 'draft').length} icon={FileText} color="gray" />
              <MetricCard title="Scheduled" value={content.filter(c => c.status === 'scheduled').length} icon={Calendar} color="purple" />
              <MetricCard title="Hidden SEO" value={content.filter(c => c.is_hidden_seo).length} icon={EyeOff} color="orange" />
            </div>

            {/* Content List */}
            <div className="bg-black border border-gray-400 rounded-xl overflow-hidden hover:border-white transition-all duration-200">
              <div className="p-4 border-b border-gray-600 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">All Content</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowSEOBlogModal(true)}
                    className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-sm rounded-lg flex items-center gap-1"
                    data-testid="new-seo-blog-btn"
                  >
                    <EyeOff size={14} /> Hidden SEO Blog
                  </button>
                  <button
                    onClick={() => { setSelectedContent(null); setShowContentModal(true); }}
                    className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-sm rounded-lg flex items-center gap-1"
                  >
                    <Plus size={14} /> New Content
                  </button>
                </div>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-600 hover:bg-gray-900">
                    <th className="text-left px-4 py-3 text-sm text-gray-400 font-medium">Title</th>
                    <th className="text-left px-4 py-3 text-sm text-gray-400 font-medium">Type</th>
                    <th className="text-left px-4 py-3 text-sm text-gray-400 font-medium">Status</th>
                    <th className="text-left px-4 py-3 text-sm text-gray-400 font-medium">Keyword</th>
                    <th className="text-left px-4 py-3 text-sm text-gray-400 font-medium">Traffic</th>
                    <th className="text-left px-4 py-3 text-sm text-gray-400 font-medium">Conversions</th>
                    <th className="text-right px-4 py-3 text-sm text-gray-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {content.map(item => (
                    <tr key={item.id} className="border-b border-gray-600 hover:hover:bg-gray-900">
                      <td className="px-4 py-3">
                        <div className="text-white font-medium flex items-center gap-2">
                          {item.title}
                          {item.is_hidden_seo && (
                            <span className="text-xs bg-orange-500/20 text-orange-400 px-1.5 py-0.5 rounded">Hidden</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">Updated: {item.last_updated?.split('T')[0]}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-300 capitalize">{item.type?.replace('_', ' ')}</span>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={item.status} />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-400">{item.target_keyword || '-'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-300">{item.traffic?.toLocaleString() || 0}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-green-400">{item.conversions || 0}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button className="p-1.5 hover:bg-gray-700 rounded text-gray-400">
                            <Edit size={14} />
                          </button>
                          <button className="p-1.5 hover:bg-gray-700 rounded text-red-400">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Brand Presence Tab */}
        {activeTab === 'brand' && brandPresence && (
          <div className="space-y-6">
            {/* Google Presence */}
            <div className="grid grid-cols-4 gap-4">
              <MetricCard title="Indexed Pages" value={brandPresence.google_presence.indexed_pages} icon={Globe} />
              <MetricCard title="Search Impressions" value={brandPresence.google_presence.search_impressions_month.toLocaleString()} icon={Eye} subtitle="This month" />
              <MetricCard title="Avg. Position" value={brandPresence.google_presence.avg_position} icon={TrendingUp} />
              <MetricCard title="Branded Searches" value={brandPresence.google_presence.branded_searches.toLocaleString()} icon={Search} />
            </div>

            {/* Social Profiles & Reviews */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Social Profiles */}
              <div className="bg-black border border-gray-400 rounded-xl p-6 hover:border-white transition-all duration-200">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Share2 className="text-cyan-400" size={20} />
                  Social Profiles
                </h3>
                <div className="space-y-3">
                  {brandPresence.social_profiles.map((profile, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-black border border-gray-600 rounded-lg hover:border-gray-400 transition-all duration-200">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-700 rounded-lg flex items-center justify-center text-white font-bold">
                          {profile.platform.charAt(0)}
                        </div>
                        <div>
                          <div className="text-white font-medium">{profile.platform}</div>
                          <div className="text-xs text-gray-500">{profile.followers.toLocaleString()} followers</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-300">{profile.engagement_rate}% engagement</div>
                        <StatusBadge status={profile.health} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Reviews */}
              <div className="bg-black border border-gray-400 rounded-xl p-6 hover:border-white transition-all duration-200">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Star className="text-yellow-400" size={20} />
                  Reviews & Ratings
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-black border border-gray-600 rounded-lg hover:border-gray-400 transition-all duration-200 text-center">
                    <div className="flex items-center justify-center gap-1 text-yellow-400 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={16} fill={i < Math.floor(brandPresence.reviews.google_rating) ? 'currentColor' : 'none'} />
                      ))}
                    </div>
                    <div className="text-2xl font-bold text-white">{brandPresence.reviews.google_rating}</div>
                    <div className="text-sm text-gray-400">Google ({brandPresence.reviews.google_reviews} reviews)</div>
                  </div>
                  <div className="p-4 bg-black border border-gray-600 rounded-lg hover:border-gray-400 transition-all duration-200 text-center">
                    <div className="flex items-center justify-center gap-1 text-yellow-400 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={16} fill={i < Math.floor(brandPresence.reviews.yelp_rating) ? 'currentColor' : 'none'} />
                      ))}
                    </div>
                    <div className="text-2xl font-bold text-white">{brandPresence.reviews.yelp_rating}</div>
                    <div className="text-sm text-gray-400">Yelp ({brandPresence.reviews.yelp_reviews} reviews)</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Directory Listings */}
            <div className="bg-black border border-gray-400 rounded-xl p-6 hover:border-white transition-all duration-200">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Briefcase className="text-cyan-400" size={20} />
                Directory Listings
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {brandPresence.directory_listings.map((listing, idx) => (
                  <div key={idx} className={`p-4 rounded-lg border ${
                    listing.claimed && listing.complete 
                      ? 'bg-green-500/10 border-green-500/30' 
                      : listing.claimed 
                      ? 'bg-yellow-500/10 border-yellow-500/30'
                      : 'hover:bg-gray-900 border-gray-600'
                  }`}>
                    <div className="text-white font-medium text-sm">{listing.directory}</div>
                    <div className="flex items-center gap-2 mt-2">
                      {listing.claimed ? (
                        <CheckCircle size={14} className="text-green-400" />
                      ) : (
                        <X size={14} className="text-gray-500" />
                      )}
                      <span className="text-xs text-gray-400">Claimed</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      {listing.complete ? (
                        <CheckCircle size={14} className="text-green-400" />
                      ) : (
                        <X size={14} className="text-gray-500" />
                      )}
                      <span className="text-xs text-gray-400">Complete</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* AI Insights */}
            <div className="bg-black border border-gray-400 rounded-xl p-6 hover:border-white transition-all duration-200">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Sparkles className="text-purple-400" size={20} />
                Brand Insights
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {brandPresence.ai_insights.map((insight, idx) => (
                  <InsightCard key={idx} insight={insight} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Attribution Tab */}
        {activeTab === 'attribution' && attribution && (
          <div className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-4 gap-4">
              <MetricCard title="Total Leads" value={attribution.summary.total_leads} icon={Users} />
              <MetricCard title="Avg. Cost per Lead" value={`$${attribution.summary.avg_cost_per_lead}`} icon={DollarSign} />
              <MetricCard title="Best Channel" value={attribution.summary.best_channel} icon={Award} color="green" />
              <MetricCard title="Best Campaign" value={attribution.summary.best_campaign} icon={Megaphone} color="green" />
            </div>

            {/* By Source & Campaign */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* By Source */}
              <div className="bg-black border border-gray-400 rounded-xl p-6 hover:border-white transition-all duration-200">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <PieChart className="text-cyan-400" size={20} />
                  Leads by Source
                </h3>
                <div className="space-y-3">
                  {attribution.by_source.map((source, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-black border border-gray-600 rounded-lg hover:border-gray-400 transition-all duration-200">
                      <div className="flex-1">
                        <div className="text-white font-medium">{source.source}</div>
                        <div className="text-xs text-gray-500">
                          {source.cost_per_lead > 0 ? `$${source.cost_per_lead}/lead` : 'Free'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-white">{source.leads}</div>
                        <StatusBadge status={source.quality} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* By Campaign */}
              <div className="bg-black border border-gray-400 rounded-xl p-6 hover:border-white transition-all duration-200">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Megaphone className="text-cyan-400" size={20} />
                  Leads by Campaign
                </h3>
                <div className="space-y-3">
                  {attribution.by_campaign.map((campaign, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-black border border-gray-600 rounded-lg hover:border-gray-400 transition-all duration-200">
                      <div className="flex-1">
                        <div className="text-white font-medium">{campaign.campaign}</div>
                        <div className="text-xs text-gray-500">
                          ${campaign.cost.toLocaleString()} spent
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-white">{campaign.leads} leads</div>
                        <div className="text-xs text-green-400">{campaign.roi}% ROI</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Top Converting Pages */}
            <div className="bg-black border border-gray-400 rounded-xl p-6 hover:border-white transition-all duration-200">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Target className="text-green-400" size={20} />
                Top Converting Pages
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {attribution.top_converting_pages.map((page, idx) => (
                  <div key={idx} className="p-4 bg-black border border-gray-600 rounded-lg hover:border-gray-400 transition-all duration-200">
                    <div className="text-white font-medium text-sm">{page.page}</div>
                    <div className="text-2xl font-bold text-green-400 mt-2">{page.conversions}</div>
                    <div className="text-xs text-gray-500">{page.conversion_rate}% rate</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="space-y-6">
            {/* Payment Status Banner */}
            {paymentStatus && (
              <div className={`p-4 rounded-lg flex items-center gap-3 ${
                paymentStatus.status === 'success' ? 'bg-green-500/20 border border-green-500/30' :
                paymentStatus.status === 'pending' ? 'bg-blue-500/20 border border-blue-500/30' :
                paymentStatus.status === 'error' || paymentStatus.status === 'expired' ? 'bg-red-500/20 border border-red-500/30' :
                'bg-yellow-500/20 border border-yellow-500/30'
              }`}>
                {paymentStatus.status === 'success' && <CheckCircle className="text-green-400" size={20} />}
                {paymentStatus.status === 'pending' && <Loader2 className="text-blue-400 animate-spin" size={20} />}
                {(paymentStatus.status === 'error' || paymentStatus.status === 'expired') && <XCircle className="text-red-400" size={20} />}
                <span className={`${
                  paymentStatus.status === 'success' ? 'text-green-400' :
                  paymentStatus.status === 'pending' ? 'text-blue-400' :
                  'text-red-400'
                }`}>{paymentStatus.message}</span>
                <button 
                  onClick={() => setPaymentStatus(null)}
                  className="ml-auto p-1 hover:bg-white/10 rounded"
                >
                  <X size={16} className="text-gray-400" />
                </button>
              </div>
            )}

            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white">Grow Your Business</h2>
              <p className="text-gray-400 mt-2">Professional marketing services to accelerate your results</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map(service => (
                <div 
                  key={service.id} 
                  className={`bg-black border rounded-xl p-6 hover:border-white hover:bg-gray-900 transition-all duration-200 ${
                    service.popular ? 'border-cyan-500/50 ring-1 ring-cyan-500/20' : 'border-gray-400'
                  }`}
                  data-testid={`service-card-${service.id}`}
                >
                  {service.popular && (
                    <div className="text-xs text-cyan-400 font-medium mb-2 flex items-center gap-1">
                      <Zap size={12} /> Popular
                    </div>
                  )}
                  <h3 className="text-lg font-bold text-white">{service.name}</h3>
                  <div className="text-xs text-gray-500 mb-3">{service.category}</div>
                  <p className="text-sm text-gray-400 mb-4">{service.description}</p>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Target size={14} className="text-green-400" />
                      <span className="text-gray-300">{service.outcome}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Clock size={14} className="text-blue-400" />
                      <span className="text-gray-300">{service.timeline}</span>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-600 pt-4 flex items-center justify-between">
                    <div>
                      <div className="text-xs text-gray-500">Starting at</div>
                      <div className="text-xl font-bold text-white">${service.starting_price.toLocaleString()}</div>
                    </div>
                    <button
                      onClick={() => handleStripeCheckout(service.id)}
                      disabled={isCheckingOut}
                      className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-2 transition-colors"
                      data-testid={`checkout-btn-${service.id}`}
                    >
                      {isCheckingOut ? (
                        <>
                          <Loader2 className="animate-spin" size={16} />
                          Loading...
                        </>
                      ) : (
                        <>
                          <CreditCard size={16} />
                          Buy Now
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Weekly Report */}
              {weeklyReport && (
                <div className="bg-black border border-gray-400 rounded-xl p-6 hover:border-white transition-all duration-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <Calendar className="text-cyan-400" size={20} />
                      Weekly Summary
                    </h3>
                    <span className="text-sm text-gray-400">{weeklyReport.period}</span>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-400 mb-2">Highlights</h4>
                      <ul className="space-y-1">
                        {weeklyReport.highlights.map((h, i) => (
                          <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                            <CheckCircle size={14} className="text-green-400 mt-0.5 flex-shrink-0" />
                            {h}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {Object.entries(weeklyReport.metrics).map(([key, data]) => (
                        <div key={key} className="p-3 bg-black border border-gray-600 rounded-lg hover:border-gray-400 transition-all duration-200">
                          <div className="text-xs text-gray-500 capitalize">{key.replace('_', ' ')}</div>
                          <div className="text-lg font-bold text-white">
                            {typeof data.value === 'number' ? data.value.toLocaleString() : data.value}
                          </div>
                          <div className={`text-xs ${data.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {data.change >= 0 ? '+' : ''}{data.change}%
                          </div>
                        </div>
                      ))}
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-yellow-400 mb-2">Needs Attention</h4>
                      <ul className="space-y-1">
                        {weeklyReport.needs_attention.map((n, i) => (
                          <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                            <AlertCircle size={14} className="text-yellow-400 mt-0.5 flex-shrink-0" />
                            {n}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <button className="w-full mt-4 px-4 py-2 bg-black border border-gray-500 hover:bg-gray-900 hover:border-gray-400 text-white rounded-lg flex items-center justify-center gap-2">
                    <Download size={16} />
                    Download Report
                  </button>
                </div>
              )}

              {/* Monthly Report */}
              {monthlyReport && (
                <div className="bg-black border border-gray-400 rounded-xl p-6 hover:border-white transition-all duration-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <BarChart3 className="text-purple-400" size={20} />
                      Monthly Performance
                    </h3>
                    <span className="text-sm text-gray-400">{monthlyReport.period}</span>
                  </div>

                  <p className="text-sm text-gray-300 mb-4">{monthlyReport.summary}</p>

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {Object.entries(monthlyReport.key_metrics).map(([key, data]) => (
                      <div key={key} className="p-3 bg-black border border-gray-600 rounded-lg hover:border-gray-400 transition-all duration-200">
                        <div className="text-xs text-gray-500 capitalize">{key.replace('_', ' ')}</div>
                        <div className="text-lg font-bold text-white">
                          {key.includes('revenue') || key.includes('cost') ? '$' : ''}
                          {typeof data.value === 'number' ? data.value.toLocaleString() : data.value}
                        </div>
                        {data.vs_last_month !== undefined && (
                          <div className={`text-xs ${data.vs_last_month >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {data.vs_last_month >= 0 ? '+' : ''}{data.vs_last_month}% vs last month
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-cyan-400 mb-2">Recommendations</h4>
                    <ul className="space-y-1">
                      {monthlyReport.recommendations.map((r, i) => (
                        <li key={i} className="text-sm text-gray-300 flex items-start gap-2">
                          <Lightbulb size={14} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button className="w-full mt-4 px-4 py-2 bg-black border border-gray-500 hover:bg-gray-900 hover:border-gray-400 text-white rounded-lg flex items-center justify-center gap-2">
                    <Download size={16} />
                    Download Report
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Service Order Modal */}
      {showServiceModal && selectedService && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowServiceModal(false)}>
          <div className="bg-black border border-gray-400 rounded-xl w-full max-w-md hover:border-white transition-all duration-200" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-600">
              <h3 className="text-xl font-bold text-white">Order Service</h3>
              <p className="text-gray-400 text-sm mt-1">Complete your purchase</p>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-white">{selectedService.name}</h4>
                <p className="text-sm text-gray-400 mt-1">{selectedService.description}</p>
              </div>
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Timeline</span>
                  <span className="text-white">{selectedService.timeline}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Starting Price</span>
                  <span className="text-white text-lg font-bold">${selectedService.starting_price.toLocaleString()}</span>
                </div>
              </div>
              <div className="hover:bg-gray-900 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 text-green-400 mb-2">
                  <CheckCircle size={16} />
                  <span className="text-sm font-medium">What you'll get:</span>
                </div>
                <p className="text-sm text-gray-300">{selectedService.outcome}</p>
              </div>
            </div>
            <div className="p-6 border-t border-gray-600 flex gap-3">
              <button
                onClick={() => setShowServiceModal(false)}
                className="flex-1 px-4 py-3 bg-black border border-gray-500 hover:bg-gray-900 text-gray-300 rounded-lg transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={() => handleOrderService(selectedService.id)}
                className="flex-1 px-4 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg flex items-center justify-center gap-2"
              >
                <CreditCard size={18} />
                Place Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Modal */}
      {showCampaignModal && (
        <CampaignModal
          campaign={selectedCampaign}
          onClose={() => { setShowCampaignModal(false); setSelectedCampaign(null); }}
          onSave={handleCreateCampaign}
        />
      )}

      {/* AI Assistant Panel */}
      <AIAssistantPanel isOpen={showAIPanel} onClose={() => setShowAIPanel(false)} />

      {/* SEO Blog Creation Modal */}
      <SEOBlogModal 
        isOpen={showSEOBlogModal} 
        onClose={() => setShowSEOBlogModal(false)} 
        onSave={handleCreateSEOBlog}
      />

      {/* Content Modal */}
      <ContentModal
        isOpen={showContentModal}
        onClose={() => { setShowContentModal(false); setSelectedContent(null); }}
        onSave={handleSaveContent}
        content={selectedContent}
      />
    </div>
  );
};

// Campaign Modal Component
const CampaignModal = ({ campaign, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: campaign?.name || '',
    type: campaign?.type || 'social',
    status: campaign?.status || 'planned',
    goal: campaign?.goal || 'traffic',
    budget: campaign?.budget || '',
    start_date: campaign?.start_date || '',
    end_date: campaign?.end_date || '',
    owner: campaign?.owner || '',
    notes: campaign?.notes || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      budget: formData.budget ? parseFloat(formData.budget) : null
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-black border border-gray-400 rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden hover:border-white transition-all duration-200" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-600">
          <h3 className="text-xl font-bold text-white">{campaign ? 'Edit Campaign' : 'New Campaign'}</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Campaign Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Type</label>
              <select
                value={formData.type}
                onChange={e => setFormData({...formData, type: e.target.value})}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
              >
                <option value="social">Social Media</option>
                <option value="paid_ads">Paid Ads</option>
                <option value="website">Website</option>
                <option value="seo">SEO</option>
                <option value="email">Email</option>
                <option value="launch">Product Launch</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Goal</label>
              <select
                value={formData.goal}
                onChange={e => setFormData({...formData, goal: e.target.value})}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
              >
                <option value="traffic">Traffic</option>
                <option value="leads">Leads</option>
                <option value="awareness">Awareness</option>
                <option value="sales">Sales</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Start Date</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={e => setFormData({...formData, start_date: e.target.value})}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">End Date</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={e => setFormData({...formData, end_date: e.target.value})}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Budget ($)</label>
              <input
                type="number"
                value={formData.budget}
                onChange={e => setFormData({...formData, budget: e.target.value})}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                placeholder="Optional"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Owner</label>
              <input
                type="text"
                value={formData.owner}
                onChange={e => setFormData({...formData, owner: e.target.value})}
                className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                placeholder="Team or person"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={e => setFormData({...formData, notes: e.target.value})}
              className="w-full px-4 py-2 bg-black border border-gray-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
              rows={3}
            />
          </div>
        </form>
        <div className="p-6 border-t border-gray-600 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-black border border-gray-500 hover:bg-gray-900 hover:border-gray-400 text-gray-300 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg"
          >
            {campaign ? 'Update' : 'Create'} Campaign
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarketingHub;
